import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import {
  Search,
  Download,
  Edit2,
  Trash2,
  Coins,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  X,
  Save,
  Clock,
  User
} from "lucide-react";
import API_BASE from "../config/api";
import { exportTradesToCSV } from "../utils/csvExport";
import { ConfirmModal } from "./admin/AdminModals";
import { formatTradeId } from "../utils/tradeIdFormatter";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    gap: 0.85rem;
  }
`;

const FilterCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  padding: 1.15rem 1.35rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 0.85rem;
    border-radius: 10px;
    gap: 0.75rem;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;

  svg {
    position: absolute;
    left: 0.85rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
  }

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: #0b0f19;
  border: 1px solid #1f293d;
  border-radius: 8px;
  padding: 0.6rem 0.85rem 0.6rem 2.4rem;
  color: #f3f4f6;
  font-size: 0.88rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
  }

  &::placeholder {
    color: #6b7280;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const FiltersGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: auto;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.45rem;
    width: 100%;
  }
`;

const Select = styled.select`
  background: #0b0f19;
  border: 1px solid #1f293d;
  color: #d1d5db;
  border-radius: 8px;
  padding: 0.55rem 0.8rem;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    border-color: #f59e0b;
  }

  option {
    background: #111827;
    color: #fff;
  }

  @media (max-width: 640px) {
    font-size: 16px;
    padding: 0.5rem;
    width: 100%;
  }
`;

const DateInput = styled.input`
  background: #0b0f19;
  border: 1px solid #1f293d;
  color: #d1d5db;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  color-scheme: dark;

  &:focus {
    border-color: #f59e0b;
  }

  @media (max-width: 768px) {
    grid-column: 1 / -1; /* full width on mobile */
    font-size: 16px;
    width: 100%;
  }
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  background: #1f293d;
  color: #d1d5db;
  border: 1px solid #2b3648;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #fff;
  }

  &.export {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border-color: rgba(16, 185, 129, 0.3);

    &:hover {
      background: rgba(16, 185, 129, 0.25);
    }
  }

  &.reset {
    background: #1f2937;
    color: #9ca3af;
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.8rem;
    width: 100%;
  }
`;

const SummaryBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  background: #0b0f19;
  border: 1px solid #1f293d;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  @media (max-width: 340px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  overflow: hidden;

  .label {
    font-size: 0.68rem;
    color: #9ca3af;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .val {
    font-size: 1.05rem;
    font-weight: 700;
    color: ${(props) => props.$color || "#f3f4f6"};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 640px) {
      font-size: 0.95rem;
    }
  }
`;

const TableCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.25);
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    border-radius: 10px;
  }
`;

/* 🖥️ Desktop Table */
const DesktopTableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;

  @media (max-width: 800px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.88rem;

  th {
    background: #0e1422;
    padding: 0.9rem 1.25rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.6px;
    border-bottom: 1px solid #1f293d;
    white-space: nowrap;
  }

  td {
    padding: 0.95rem 1.25rem;
    border-bottom: 1px solid #1a2234;
    color: #d1d5db;
    vertical-align: middle;
  }

  tbody tr:hover {
    background-color: #161f30;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  &.buy {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }

  &.sell {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }

  &.gold {
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.3);
  }

  &.silver {
    background: rgba(148, 163, 184, 0.15);
    color: #cbd5e1;
    border: 1px solid rgba(148, 163, 184, 0.3);
  }
`;

/* 📱 Mobile Trade Cards (Active on <= 800px) */
const MobileTradesContainer = styled.div`
  display: none;
  padding: 0.75rem;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 800px) {
    display: flex;
  }
`;

const MobileTradeCard = styled.div`
  background: #161f30;
  border: 1px solid #1f293d;
  border-radius: 11px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const MobileTradeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
  min-width: 0;
`;

const MobileTradeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.45rem 0;
  border-top: 1px solid #1f293d;
  border-bottom: 1px solid #1f293d;
  min-width: 0;

  .customer-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.88rem;
    font-weight: 600;
    color: #f9fafb;
    gap: 0.5rem;
    min-width: 0;

    .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
      flex: 1;
    }

    .phone {
      font-size: 0.75rem;
      color: #9ca3af;
      font-weight: 400;
      flex-shrink: 0;
    }
  }

  .meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;
    color: #9ca3af;
    gap: 0.5rem;
  }

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.15rem;
    padding-top: 0.35rem;
    border-top: 1px dashed #1f293d;
    gap: 0.5rem;
  }
`;

const MobileTradeFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem;
  width: 100%;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0.45rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
  }

  .edit-btn {
    background: #1f293d;
    color: #e5e7eb;
    border: 1px solid #374151;
  }

  .del-btn {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid #1f293d;
  flex-wrap: wrap;
  gap: 0.65rem;
  font-size: 0.85rem;
  color: #9ca3af;
  width: 100%;

  @media (max-width: 640px) {
    padding: 0.75rem 0.85rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const PageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;

  @media (max-width: 640px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const PageBtn = styled.button`
  background: ${(props) => (props.$active ? "#f59e0b" : "#1f293d")};
  color: ${(props) => (props.$active ? "#0b0f19" : "#d1d5db")};
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  border: 1px solid ${(props) => (props.$active ? "#f59e0b" : "#2b3648")};
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$active ? "#f59e0b" : "#374151")};
    color: ${(props) => (props.$active ? "#0b0f19" : "#fff")};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ActionIconBtn = styled.button`
  background: #1f293d;
  border: 1px solid #2b3648;
  color: #9ca3af;
  border-radius: 6px;
  padding: 0.4rem 0.55rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #f3f4f6;
  }

  &.edit:hover {
    color: #f59e0b;
    border-color: #d97706;
  }

  &.delete:hover {
    color: #ef4444;
    border-color: #dc2626;
  }
`;

// Responsive Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 0.85rem;
`;

const ModalContent = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.25rem 1rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.6);
  color: #fff;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.15rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #1f293d;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #f3f4f6;
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 0.95rem;

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input, select {
    width: 100%;
    background: #0b0f19;
    border: 1px solid #1f293d;
    border-radius: 8px;
    padding: 0.6rem 0.8rem;
    color: #f3f4f6;
    font-size: 0.9rem;
    outline: none;
    transition: all 0.2s;

    &:focus {
      border-color: #f59e0b;
    }

    @media (max-width: 640px) {
      font-size: 16px;
    }
  }

  select option {
    background: #111827;
  }
`;

const PricePreview = styled.div`
  background: #0b0f19;
  border: 1px dashed rgba(245, 158, 11, 0.4);
  border-radius: 8px;
  padding: 0.75rem;
  margin: 1.15rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .label {
    font-size: 0.8rem;
    color: #9ca3af;
  }
  .amount {
    font-size: 1.15rem;
    font-weight: 700;
    color: #f59e0b;
  }
`;

const AdminTradesSection = ({ initialTrades, onNotify, onTradesUpdated }) => {
  const [trades, setTrades] = useState(initialTrades || []);
  const [loading, setLoading] = useState(!initialTrades);
  const [search, setSearch] = useState("");
  const [metalFilter, setMetalFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [editingTrade, setEditingTrade] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingTradeId, setDeletingTradeId] = useState(null);

  useEffect(() => {
    if (initialTrades) {
      setTrades(
        Array.isArray(initialTrades)
          ? initialTrades.filter((t) => t.status !== "pending_payment")
          : []
      );
    } else {
      fetchTrades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTrades]);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/trades`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken") || localStorage.getItem("token") || ""}`,
        },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        const submitted = data.filter((t) => t.status !== "pending_payment");
        setTrades(submitted);
        onTradesUpdated?.(submitted);
      }
    } catch (err) {
      console.error("Failed to fetch trades:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrades = useMemo(() => {
    return trades
      .filter((trade) => trade.status && trade.status !== "pending_payment")
      .filter((trade) => {
        if (search) {
          const q = search.toLowerCase();
          const tradeId = formatTradeId(trade).toLowerCase();
          const customerName = (trade.fullName || (trade.userId && trade.userId.fullName) || "").toLowerCase();
          const customerPhone = (trade.phone || (trade.userId && trade.userId.phone) || "").toLowerCase();
          const metal = (trade.metal || "").toLowerCase();
          const action = (trade.action || "").toLowerCase();
          const rate = String(trade.rate || "");
          const total = String(trade.totalPrice || "");

          const match =
            tradeId.includes(q) ||
            customerName.includes(q) ||
            customerPhone.includes(q) ||
            metal.includes(q) ||
            action.includes(q) ||
            rate.includes(q) ||
            total.includes(q);

          if (!match) return false;
        }

        if (metalFilter !== "all") {
          const metal = (trade.metal || "").toLowerCase();
          if (metalFilter === "gold" && !metal.includes("gold")) return false;
          if (metalFilter === "silver" && !metal.includes("silver")) return false;
        }

        if (actionFilter !== "all") {
          if (trade.action?.toLowerCase() !== actionFilter.toLowerCase()) return false;
        }

        if (dateFilter) {
          const tradeDate = new Date(trade.timestamp).toISOString().split("T")[0];
          if (tradeDate !== dateFilter) return false;
        }

        return true;
      });
  }, [trades, search, metalFilter, actionFilter, dateFilter]);

  const summary = useMemo(() => {
    let buyTotal = 0;
    let sellTotal = 0;

    filteredTrades.forEach((t) => {
      // Declined trades are excluded from successful turnover
      if (t.status === "declined" || t.status === "pending_payment") return;
      const amt = Number(t.totalPrice) || 0;
      if (t.action?.toLowerCase() === "buy") {
        buyTotal += amt;
      } else if (t.action?.toLowerCase() === "sell") {
        sellTotal += amt;
      }
    });

    return {
      count: filteredTrades.length,
      buyTotal,
      sellTotal,
      netVolume: buyTotal + sellTotal,
    };
  }, [filteredTrades]);

  const totalPages = Math.ceil(filteredTrades.length / pageSize) || 1;
  const paginatedTrades = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTrades.slice(start, start + pageSize);
  }, [filteredTrades, currentPage, pageSize]);

  const resetFilters = () => {
    setSearch("");
    setMetalFilter("all");
    setActionFilter("all");
    setDateFilter("");
    setCurrentPage(1);
  };

  const calculateEstimatedTotal = () => {
    if (!editingTrade) return 0;
    const isGold = (editingTrade.metal || "").toLowerCase().includes("gold");
    const numericRate = parseFloat(editingTrade.rate) || 0;
    const numericQty = parseFloat(editingTrade.quantity) || 0;
    const perGramRate = isGold ? numericRate / 10 : numericRate;
    return +(perGramRate * numericQty).toFixed(2);
  };

  const handleUpdateTrade = async (e) => {
    e.preventDefault();
    if (!editingTrade) return;
    setSavingEdit(true);

    try {
      const res = await fetch(`${API_BASE}/api/trades/${editingTrade._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          metal: editingTrade.metal,
          action: editingTrade.action,
          rate: editingTrade.rate,
          quantity: editingTrade.quantity,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        const updated = trades.map((t) => (t._id === editingTrade._id ? data.trade || editingTrade : t));
        setTrades(updated);
        onTradesUpdated?.(updated);
        setEditingTrade(null);
        onNotify?.({ type: "success", message: "Trade record updated successfully!" });
      } else {
        onNotify?.({ type: "error", message: data.message || "Failed to update trade." });
      }
    } catch (err) {
      console.error("Update trade error:", err);
      onNotify?.({ type: "error", message: "Server connection failed." });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTrade = async () => {
    if (!deletingTradeId) return;
    try {
      const res = await fetch(`${API_BASE}/api/trades/${deletingTradeId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      if (res.ok) {
        const updated = trades.filter((t) => t._id !== deletingTradeId);
        setTrades(updated);
        onTradesUpdated?.(updated);
        onNotify?.({ type: "success", message: "Trade deleted successfully." });
      } else {
        onNotify?.({ type: "error", message: "Failed to delete trade." });
      }
    } catch (err) {
      console.error("Delete trade error:", err);
      onNotify?.({ type: "error", message: "Error deleting trade." });
    } finally {
      setDeletingTradeId(null);
    }
  };

  return (
    <Wrapper>
      {/* 🛠️ Filters & Controls Card */}
      <FilterCard>
        <FilterRow>
          <SearchInputWrap>
            <Search size={15} />
            <SearchInput
              type="text"
              placeholder="Search customer, metal, amount..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </SearchInputWrap>

          <FiltersGroup>
            {/* Metal Select */}
            <Select
              value={metalFilter}
              onChange={(e) => {
                setMetalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Metals</option>
              <option value="gold">Gold Only</option>
              <option value="silver">Silver Only</option>
            </Select>

            {/* Action Select */}
            <Select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Orders</option>
              <option value="buy">BUY Only</option>
              <option value="sell">SELL Only</option>
            </Select>

            {/* Date Input */}
            <DateInput
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
            />

            {/* Reset */}
            {(search || metalFilter !== "all" || actionFilter !== "all" || dateFilter) && (
              <ActionBtn className="reset" onClick={resetFilters}>
                <X size={13} /> Clear
              </ActionBtn>
            )}

            {/* CSV Export */}
            <ActionBtn
              className="export"
              onClick={() => exportTradesToCSV(filteredTrades, `GJ_Bullion_Trades_${new Date().toISOString().slice(0, 10)}.csv`)}
            >
              <Download size={13} /> Export CSV
            </ActionBtn>
          </FiltersGroup>
        </FilterRow>

        {/* 📊 Summary of Filtered Trades */}
        <SummaryBar>
          <SummaryStat>
            <span className="label">Total Matches</span>
            <span className="val">{summary.count} Trades</span>
          </SummaryStat>
          <SummaryStat $color="#10b981">
            <span className="label">Buy Turnover</span>
            <span className="val">₹{summary.buyTotal.toLocaleString("en-IN")}</span>
          </SummaryStat>
          <SummaryStat $color="#ef4444">
            <span className="label">Sell Turnover</span>
            <span className="val">₹{summary.sellTotal.toLocaleString("en-IN")}</span>
          </SummaryStat>
          <SummaryStat $color="#f59e0b">
            <span className="label">Gross Volume</span>
            <span className="val">₹{summary.netVolume.toLocaleString("en-IN")}</span>
          </SummaryStat>
        </SummaryBar>
      </FilterCard>

      {/* 🧾 Trades Display */}
      <TableCard>
        {/* 🖥️ Desktop Table View */}
        <DesktopTableWrapper>
          <Table>
            <thead>
              <tr>
                <th>#</th>
                <th>Trade ID</th>
                <th>Date & Time</th>
                <th>Customer</th>
                <th>Metal</th>
                <th>Action</th>
                <th>Rate</th>
                <th>Quantity</th>
                <th>Total Value</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
                    Loading transactions...
                  </td>
                </tr>
              ) : paginatedTrades.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>
                    No trade orders match your search criteria.
                  </td>
                </tr>
              ) : (
                paginatedTrades.map((trade, idx) => {
                  const globalIdx = (currentPage - 1) * pageSize + idx + 1;
                  const isGold = (trade.metal || "").toLowerCase().includes("gold");
                  const isBuy = trade.action?.toLowerCase() === "buy";
                  const customerName = trade.fullName || (trade.userId && trade.userId.fullName) || "Anonymous";
                  const customerPhone = trade.phone || (trade.userId && trade.userId.phone) || "";

                  return (
                    <tr key={trade._id || idx}>
                      <td style={{ color: "#6b7280", fontWeight: 500 }}>{globalIdx}</td>
                      <td>
                        <span style={{ color: "#f5d76e", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.5px" }}>
                          {formatTradeId(trade, idx, paginatedTrades.length)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
                          <span style={{ color: "#f3f4f6", fontSize: "0.86rem", fontWeight: 500 }}>
                            {new Date(trade.timestamp).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                          <span style={{ color: "#6b7280", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                            <Clock size={11} />
                            {new Date(trade.timestamp).toLocaleTimeString("en-IN", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 600, color: "#f9fafb" }}>{customerName}</span>
                          <span style={{ fontSize: "0.76rem", color: "#9ca3af" }}>{customerPhone}</span>
                        </div>
                      </td>
                      <td>
                        <Badge className={isGold ? "gold" : "silver"}>
                          <Coins size={12} />
                          {trade.metal}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={isBuy ? "buy" : "sell"}>
                          {isBuy ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {trade.action?.toUpperCase()}
                        </Badge>
                        {trade.status === "pending_verification" && (
                          <span style={{ display: "block", fontSize: "0.7rem", color: "#f5d76e", fontWeight: 700, marginTop: "3px" }}>
                            ⏳ Verification Pending
                          </span>
                        )}
                        {trade.status === "confirmed" && (
                          <span style={{ display: "block", fontSize: "0.7rem", color: "#10b981", fontWeight: 700, marginTop: "3px" }}>
                            ✅ Confirmed
                          </span>
                        )}
                        {trade.status === "declined" && (
                          <span style={{ display: "block", fontSize: "0.7rem", color: "#ef4444", fontWeight: 700, marginTop: "3px" }}>
                            ❌ Declined
                          </span>
                        )}
                      </td>
                      <td style={{ fontWeight: 600, color: "#f3f4f6" }}>
                        ₹{Number(trade.rate || 0).toLocaleString("en-IN")}
                      </td>
                      <td style={{ fontWeight: 600, color: "#d1d5db" }}>{trade.quantity}</td>
                      <td>
                        <span style={{ fontWeight: 700, color: isBuy ? "#10b981" : "#f87171", fontSize: "0.95rem" }}>
                          ₹{Number(trade.totalPrice || 0).toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem" }}>
                          <ActionIconBtn
                            className="edit"
                            title="Edit Trade"
                            onClick={() => setEditingTrade({ ...trade })}
                          >
                            <Edit2 size={13} />
                          </ActionIconBtn>
                          <ActionIconBtn
                            className="delete"
                            title="Delete Trade"
                            onClick={() => setDeletingTradeId(trade._id)}
                          >
                            <Trash2 size={13} />
                          </ActionIconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </DesktopTableWrapper>

        {/* 📱 Mobile Trade Cards (Zero horizontal overflow on phones) */}
        <MobileTradesContainer>
          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>
              Loading transactions...
            </div>
          ) : paginatedTrades.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              No trade orders match your search criteria.
            </div>
          ) : (
            paginatedTrades.map((trade, idx) => {
              const globalIdx = (currentPage - 1) * pageSize + idx + 1;
              const isGold = (trade.metal || "").toLowerCase().includes("gold");
              const isBuy = trade.action?.toLowerCase() === "buy";
              const customerName = trade.fullName || (trade.userId && trade.userId.fullName) || "Anonymous";
              const customerPhone = trade.phone || (trade.userId && trade.userId.phone) || "";

              return (
                <MobileTradeCard key={trade._id || idx}>
                  <MobileTradeHeader>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#9ca3af", fontSize: "0.74rem" }}>
                      <span style={{ color: "#f5d76e", fontWeight: 700 }}>
                        {formatTradeId(trade, idx, paginatedTrades.length)}
                      </span>
                      <span>•</span>
                      <span>
                        {new Date(trade.timestamp).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        })}
                        , {new Date(trade.timestamp).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <Badge className={isBuy ? "buy" : "sell"}>
                      {isBuy ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {trade.action?.toUpperCase()}
                    </Badge>
                  </MobileTradeHeader>

                  <MobileTradeBody>
                    <div className="customer-row">
                      <span className="name" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <User size={13} color="#f59e0b" style={{ flexShrink: 0 }} />
                        {customerName}
                      </span>
                      {customerPhone && <span className="phone">{customerPhone}</span>}
                    </div>

                    <div className="meta-row">
                      <Badge className={isGold ? "gold" : "silver"}>
                        <Coins size={11} />
                        {trade.metal}
                      </Badge>
                      <span style={{ color: "#d1d5db", fontWeight: 600 }}>
                        Qty: {trade.quantity}
                      </span>
                      {trade.status === "pending_verification" && (
                        <span style={{ color: "#f5d76e", fontSize: "0.72rem", fontWeight: 700 }}>
                          ⏳ Pending
                        </span>
                      )}
                      {trade.status === "confirmed" && (
                        <span style={{ color: "#10b981", fontSize: "0.72rem", fontWeight: 700 }}>
                          ✅ Confirmed
                        </span>
                      )}
                      {trade.status === "declined" && (
                        <span style={{ color: "#ef4444", fontSize: "0.72rem", fontWeight: 700 }}>
                          ❌ Declined
                        </span>
                      )}
                    </div>

                    <div className="price-row">
                      <span style={{ color: "#9ca3af", fontSize: "0.78rem" }}>
                        Rate: ₹{Number(trade.rate || 0).toLocaleString("en-IN")}
                      </span>
                      <span style={{ fontWeight: 800, color: isBuy ? "#10b981" : "#f87171", fontSize: "1rem" }}>
                        ₹{Number(trade.totalPrice || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                  </MobileTradeBody>

                  <MobileTradeFooter>
                    <button className="edit-btn" onClick={() => setEditingTrade({ ...trade })}>
                      <Edit2 size={12} /> Edit Trade
                    </button>
                    <button className="del-btn" onClick={() => setDeletingTradeId(trade._id)}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </MobileTradeFooter>
                </MobileTradeCard>
              );
            })
          )}
        </MobileTradesContainer>

        {/* 📄 Pagination Bar */}
        {filteredTrades.length > 0 && (
          <PaginationBar>
            <div>
              Showing{" "}
              <strong>
                {Math.min((currentPage - 1) * pageSize + 1, filteredTrades.length)} -{" "}
                {Math.min(currentPage * pageSize, filteredTrades.length)}
              </strong>{" "}
              of <strong>{filteredTrades.length}</strong> trades
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <span>Rows:</span>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{ padding: "0.25rem 0.4rem", fontSize: "0.8rem" }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </Select>
              </div>

              <PageControls>
                <PageBtn
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </PageBtn>

                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#f3f4f6", padding: "0 0.35rem" }}>
                  {currentPage} / {totalPages}
                </span>

                <PageBtn
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </PageBtn>
              </PageControls>
            </div>
          </PaginationBar>
        )}
      </TableCard>

      {/* ✏️ Edit Trade Modal */}
      {editingTrade && (
        <ModalOverlay onClick={() => setEditingTrade(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                <Edit2 size={17} color="#f59e0b" />
                Modify Trade Order
              </h3>
              <button
                onClick={() => setEditingTrade(null)}
                style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}
              >
                <X size={17} />
              </button>
            </ModalHeader>

            <form onSubmit={handleUpdateTrade}>
              <FormGroup>
                <label>Metal Name</label>
                <input
                  type="text"
                  required
                  value={editingTrade.metal || ""}
                  onChange={(e) => setEditingTrade({ ...editingTrade, metal: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <label>Order Action</label>
                <select
                  value={editingTrade.action?.toLowerCase() || "buy"}
                  onChange={(e) => setEditingTrade({ ...editingTrade, action: e.target.value })}
                >
                  <option value="buy">BUY</option>
                  <option value="sell">SELL</option>
                </select>
              </FormGroup>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.65rem" }}>
                <FormGroup>
                  <label>Trading Rate (₹)</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={editingTrade.rate || ""}
                    onChange={(e) => setEditingTrade({ ...editingTrade, rate: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Quantity</label>
                  <input
                    type="text"
                    required
                    value={editingTrade.quantity || ""}
                    onChange={(e) => setEditingTrade({ ...editingTrade, quantity: e.target.value })}
                  />
                </FormGroup>
              </div>

              <PricePreview>
                <span className="label">Estimated Total Price:</span>
                <span className="amount">₹{calculateEstimatedTotal().toLocaleString("en-IN")}</span>
              </PricePreview>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.65rem", marginTop: "1rem" }}>
                <button
                  type="button"
                  onClick={() => setEditingTrade(null)}
                  style={{
                    background: "#1f2937",
                    color: "#e5e7eb",
                    border: "1px solid #374151",
                    borderRadius: "7px",
                    padding: "0.55rem 0.95rem",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  style={{
                    background: "linear-gradient(135deg, #f59e0b, #d97706)",
                    color: "#0b0f19",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: "7px",
                    padding: "0.55rem 1.15rem",
                    cursor: savingEdit ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <Save size={14} />
                  {savingEdit ? "Updating..." : "Update Trade"}
                </button>
              </div>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 🗑️ Delete Trade Confirmation */}
      <ConfirmModal
        isOpen={Boolean(deletingTradeId)}
        title="Delete Trade Record"
        message="Are you sure you want to delete this trade order? This transaction will be permanently removed from reports."
        confirmText="Yes, Delete Trade"
        isDanger={true}
        onConfirm={handleDeleteTrade}
        onCancel={() => setDeletingTradeId(null)}
      />
    </Wrapper>
  );
};

export default AdminTradesSection;
